/* ─────────────────────────────────────────────────────────────
   KUNDA REQUESTS — The Connection Loop (Long-Term, V1)

   discovery → request → host responds → share location →
   share contact → complete.

   One continuous lifecycle per guest+listing. No chat, no
   payments. KUNDA owns trust and the funnel; the host owns the
   property, exact location, and contact; the guest owns whether
   to proceed. Private location is only ever copied into a request
   BY THE HOST, at the moment they choose to share it.

   Email notifications are a later increment — everything here is
   in-platform, driven by two "unseen" flags + the bell.
───────────────────────────────────────────────────────────────── */
import React, { useState } from "react";
import { SubpageHeader, IconSend, IconCheck, IconPinLine, IconPhoneLine, IconCalendarLine, IconHomeLine } from "./ui";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";

/* ── Status labels (guest-facing language) ─────────────────── */
var STATUS_META = {
  pending:           { label: "Waiting for host",   cls: "rq-s-pending" },
  viewing_proposed:  { label: "Viewing proposed",   cls: "rq-s-viewing" },
  contact_shared:    { label: "Contact shared",     cls: "rq-s-accepted" },
  accepted:          { label: "Accepted",           cls: "rq-s-accepted" },
  declined:  { label: "Declined",           cls: "rq-s-declined" },
  completed: { label: "Completed",          cls: "rq-s-completed" },
};
function statusMeta(s) { return STATUS_META[s] || STATUS_META.pending; }

export function activeRequestFor(requests, listingId) {
  return (requests || []).find(function(r) {
    return r.listingId === listingId &&
      (r.status === "pending" || r.status === "viewing_proposed" ||
       r.status === "accepted" || r.status === "contact_shared");
  }) || null;
}

/* "2026-08-12" → "12 August 2026" (falls back to the raw value if unparseable) */
/* Human label for a request type. "contact" is the current flow;
   "rent"/"viewing" are legacy and still render for older requests. */
/* Strips spaces/symbols and adds the Gambia country code when the host
   typed a local number, so the WhatsApp link always resolves. */
function waNumber(raw) {
  var d = String(raw || "").replace(/[^0-9]/g, "");
  if (!d) return "";
  if (d.indexOf("00") === 0) d = d.slice(2);
  if (d.indexOf("220") === 0) return d;
  if (d.length <= 9) return "220" + d;
  return d;
}

/* What the guest is asking for, phrased for the host. "contact" is the
   current flow; the other two only appear on pre-existing requests. */
function reqIntent(t) {
  if (t === "rent")    return "wants to rent";
  if (t === "viewing") return "wants a viewing";
  return "requested contact";
}

function reqTypeLabel(t) {
  if (t === "contact") return "Contact";
  if (t === "rent")    return "Rental";
  return "Viewing";
}

function fmtViewingDate(s) {
  if (!s) return "";
  var parts = String(s).split("-");
  if (parts.length !== 3) return s;
  var months = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];
  var mi = Number(parts[1]) - 1;
  if (mi < 0 || mi > 11) return s;
  return Number(parts[2]) + " " + months[mi] + " " + parts[0];
}

/* "17:00" → "5:00 PM" (falls back to the raw value if unparseable) */
function fmtViewingTime(s) {
  if (!s) return "";
  var parts = String(s).split(":");
  if (parts.length < 2) return s;
  var h = Number(parts[0]);
  var m = parts[1];
  if (isNaN(h)) return s;
  var suffix = h >= 12 ? "PM" : "AM";
  var h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return h12 + ":" + m + " " + suffix;
}

/* NOTE: fmtWhen formats createdAt — i.e. WHEN THE REQUEST WAS SENT.
   It must never be used to display the requested viewing date. */
function fmtWhen(ts) {
  try {
    var d = ts && ts.seconds ? new Date(ts.seconds * 1000) : null;
    if (!d) return "";
    var m = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return d.getDate() + " " + m[d.getMonth()];
  } catch (e) { return ""; }
}

/* ── GUEST: request sheet (opened from the LT detail CTAs) ─── */
export function RequestSheet(props) {
  /* props: listing, type ("viewing"|"rent"), user, myRequests,
            onClose, onToast */
  var l = props.listing;
  var [fullName, setFullName] = useState((props.user && props.user.name) || "");
  var [phone, setPhone] = useState((props.user && props.user.phone) || "");
  var [viewingDate, setViewingDate] = useState("");
  var [viewingTime, setViewingTime] = useState("");
  var [note, setNote] = useState("");
  var [moveIn, setMoveIn] = useState("");
  var [duration, setDuration] = useState(6);
  var [occupants, setOccupants] = useState(1);
  var [busy, setBusy] = useState(false);
  var [sent, setSent] = useState(false);
  var [formErr, setFormErr] = useState("");
  var todayStr = new Date().toISOString().slice(0, 10);

  var existing = activeRequestFor(props.myRequests, l.id);
  var isRent    = props.type === "rent";
  /* New single flow. Legacy "viewing"/"rent" still render for requests
     created before this change — no data migration, nothing breaks
     for anyone mid-conversation. */
  var isContact = props.type === "contact";

  async function send() {
    if (busy || existing) return;
    if (fullName.trim().length < 2) {
      setFormErr("Please enter your full name.");
      return;
    }
    if (phone.trim().length < 6) {
      setFormErr("Please enter a valid phone number.");
      return;
    }
    if (isContact && !moveIn) {
      setFormErr("Please choose when you would like to move in.");
      return;
    }
    if (isContact && moveIn < todayStr) {
      setFormErr("Your move-in date can't be in the past.");
      return;
    }
    if (!isRent && !isContact && !viewingDate) {
      setFormErr("Please choose your preferred viewing date.");
      return;
    }
    if (!isRent && !isContact && viewingDate < todayStr) {
      setFormErr("Your viewing date can't be in the past.");
      return;
    }
    if (!isRent && !isContact && !viewingTime) {
      setFormErr("Please choose a preferred time.");
      return;
    }
    if (isRent && !moveIn) {
      setFormErr("Please choose your preferred move-in date.");
      return;
    }
    if (isRent && moveIn < todayStr) {
      setFormErr("Your move-in date can't be in the past.");
      return;
    }
    setFormErr("");
    setBusy(true);
    try {
      await addDoc(collection(db, "requests"), {
        listingId:      l.id,
        listingTitle:   (l.title && l.title.trim()) || "Long-term rental",
        listingImage:   (l.photoThumbs && l.photoThumbs[0]) || (l.photos && l.photos[0]) || l.imageUrl || "",
        listingArea:    l.location || "",
        monthlyRent:    Number(l.monthlyRent) || 0,
        hostId:         l.hostId,
        guestId:        props.user.uid,
        guestName:      props.user.name || "KUNDA guest",
        fullName:       fullName.trim(),
        phone:          phone.trim(),
        type:           isContact ? "contact" : (isRent ? "rent" : "viewing"),
        viewingDate:    (!isRent && !isContact) ? viewingDate : "",
        viewingTime:    (!isRent && !isContact) ? viewingTime : "",
        moveInDate:     (isRent || isContact) ? moveIn : "",
        durationMonths: isRent ? Number(duration) : 0,
        occupants:      isRent ? Number(occupants) : 0,
        note:           note.trim(),
        status:         "pending",
        locationShared: false,
        contactShared:  false,
        unseenHost:     true,
        unseenGuest:    false,
        createdAt:      serverTimestamp(),
        updatedAt:      serverTimestamp(),
      });
      setBusy(false);
      setSent(true);
    } catch (e) {
      setBusy(false);
      props.onToast("Couldn't send your request. Check your connection and try again.");
    }
  }

  return (
    <div className="bsheet-overlay" onClick={props.onClose}>
      <div className="bsheet" onClick={function(e) { e.stopPropagation(); }}>
        <div className="bsheet-handle"/>
        {sent ? (
          <div className="rq-sent">
            <div className="rq-sent-ic">✓</div>
            <h2 className="bsheet-title">Request sent</h2>
            <p className="rq-sent-p">Your rental request has been delivered to the host. You'll receive a notification as soon as they respond.</p>
            <button className="bsheet-continue" onClick={props.onClose}>Continue browsing</button>
            <button className="rq-view-link" onClick={function() { if (props.onViewRequests) props.onViewRequests(); }}>View my requests</button>
          </div>
        ) : existing ? (
          <div className="rq-sent">
            <div className="rq-sent-ic">🕐</div>
            <h2 className="bsheet-title">Request already active</h2>
            <p className="rq-sent-p">You already have a {existing.type === "contact" ? "contact" : (existing.type === "rent" ? "rental" : "viewing")} request for this property ({statusMeta(existing.status).label.toLowerCase()}). One conversation, one thread — check the bell for updates.</p>
            <button className="bsheet-continue" onClick={props.onClose}>Okay</button>
          </div>
        ) : (
          <>
            <h2 className="bsheet-title">{isContact ? "Request for contact" : (isRent ? "Request to rent" : "Request a viewing")}</h2>
            <div className="bsheet-prop">{(l.title && l.title.trim()) || "Long-term rental"} · {l.location}</div>

            <label className="bsheet-field">
              <span className="bsheet-field-label">Full name</span>
              <input className="wiz-input" value={fullName} maxLength={80}
                     placeholder="Your full name"
                     onChange={function(e) { setFullName(e.target.value); }}/>
            </label>
            <label className="bsheet-field">
              <span className="bsheet-field-label">Phone number</span>
              <input className="wiz-input" type="tel" inputMode="tel" value={phone} maxLength={20}
                     placeholder="e.g. 220 xxx xxxx"
                     onChange={function(e) { setPhone(e.target.value); }}/>
            </label>
            <div className="rq-privacy-note">Your information is only shared if the host accepts your request.</div>

            {isContact && (
              <label className="bsheet-field">
                <span className="bsheet-field-label">When would you like to move in?</span>
                <input type="date" className="wiz-input wiz-input-time" value={moveIn} min={todayStr}
                       onChange={function(e) { setMoveIn(e.target.value); }}/>
              </label>
            )}
            {!isRent && !isContact && (
              <>
                <label className="bsheet-field">
                  <span className="bsheet-field-label">Preferred viewing date</span>
                  <input type="date" className="bsheet-date" value={viewingDate} min={todayStr}
                         onChange={function(e) { setViewingDate(e.target.value); }}/>
                </label>
                <label className="bsheet-field">
                  <span className="bsheet-field-label">Preferred time</span>
                  <input type="time" className="bsheet-date" value={viewingTime}
                         onChange={function(e) { setViewingTime(e.target.value); }}/>
                </label>
              </>
            )}
            {isRent && !isContact && (
              <>
                <label className="bsheet-field">
                  <span className="bsheet-field-label">Preferred move-in date</span>
                  <input type="date" className="bsheet-date" value={moveIn} min={todayStr}
                         onChange={function(e) { setMoveIn(e.target.value); }}/>
                </label>
                <label className="bsheet-field">
                  <span className="bsheet-field-label">Rental duration</span>
                  <span className="rq-opt-row">
                    {[3, 6, 12, 24].map(function(n) {
                      return <button key={n} type="button" className={"fsheet-opt" + (duration === n ? " on" : "")}
                                     onClick={function() { setDuration(n); }}>{n} months</button>;
                    })}
                  </span>
                </label>
                <label className="bsheet-field">
                  <span className="bsheet-field-label">Number of occupants</span>
                  <span className="rq-opt-row">
                    {[1, 2, 3, 4, 5, 6].map(function(n) {
                      return <button key={n} type="button" className={"fsheet-opt" + (occupants === n ? " on" : "")}
                                     onClick={function() { setOccupants(n); }}>{n}</button>;
                    })}
                  </span>
                </label>
                <div className="rq-privacy-note">You are not making a payment. This is a request to rent.</div>
              </>
            )}
            <label className="bsheet-field">
              <span className="bsheet-field-label">Message to the host (optional)</span>
              <textarea className="rq-note" maxLength={300}
                        placeholder={isRent
                          ? "Hello, I am interested in renting this property. Looking forward to hearing from you."
                          : "I'm interested and would like to see the place."}
                        value={note}
                        onChange={function(e) { setNote(e.target.value); }}/>
            </label>

            {formErr ? <div className="ltw-error">{formErr}</div> : null}
            <div className="bsheet-trust">
              <div className="bsheet-trust-item">✓ No payment is made on KUNDA</div>
              <div className="bsheet-trust-item">✓ Host responds on-platform</div>
              <div className="bsheet-trust-item">✓ Location shared only if accepted</div>
              <div className="bsheet-trust-item">✓ You decide whether to proceed</div>
            </div>

            <button className={"bsheet-continue" + (busy ? " is-off" : "")} disabled={busy} onClick={send}>
              {busy ? "Sending…" : (isContact ? "Send contact request" : (isRent ? "Send rental request" : "Send viewing request"))}
            </button>
            <div className="bsheet-foot">Managed through KUNDA · One request per property</div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Shared: the lifecycle timeline ────────────────────────── */
function Timeline(props) {
  var r = props.request;
  /* Same component, two points of view — the host should never read
     guest-side copy on their own screen. */
  var forHost = !!props.forHost;
  var steps = [
    { done: true, label: reqTypeLabel(r.type) + " request sent", when: fmtWhen(r.createdAt) },
    { done: r.unseenHost === false, label: forHost ? "You viewed this request" : "Viewed by host", when: "" },
  ];
  /* Only ever shown once the host has actually proposed a viewing — never faked */
  if (r.viewingProposedAt || r.status === "viewing_proposed") {
    steps.push({
      done: !!r.viewingProposedAt,
      label: "Host proposed a viewing" + (r.proposedDate ? " · " + r.proposedDate + (r.proposedTime ? " at " + r.proposedTime : "") : ""),
      when: fmtWhen(r.viewingProposedAt)
    });
  }
  if (r.type === "contact") {
    /* Contact flow: Request sent → Host reviewed → Contact shared →
       You can contact host. Each step only completes once it truly has. */
    steps.push({
      done: r.status !== "pending",
      label: r.status === "declined"
        ? (forHost ? "You declined this request" : "Declined by host")
        : (forHost ? "You reviewed this request" : "Host reviewed your request"),
      when: fmtWhen(r.contactSharedAt || r.declinedAt)
    });
    if (r.status !== "declined") {
      steps.push({
        done: !!r.contactShared,
        label: forHost ? "You shared your contact" : "Contact shared",
        when: fmtWhen(r.contactSharedAt)
      });
      steps.push({
        done: !!r.contactShared,
        label: forHost ? "The guest can now contact you" : "You can contact the host",
        when: ""
      });
    }
  } else {
    /* Legacy viewing/rent flow — unchanged for requests created before
       the single contact action. */
    if (r.status !== "viewing_proposed") {
      steps.push({
        done: r.status !== "pending",
        label: r.status === "declined" ? "Declined by host" : "Accepted by host",
        when: fmtWhen(r.acceptedAt || r.declinedAt)
      });
    }
    if (r.status !== "declined" && r.status !== "viewing_proposed") {
      steps.push({ done: !!r.locationShared, label: "Exact location shared", when: fmtWhen(r.locationSharedAt) });
      steps.push({ done: !!r.contactShared, label: "Host contact shared", when: fmtWhen(r.contactSharedAt) });
      steps.push({ done: r.status === "completed", label: "Completed", when: fmtWhen(r.completedAt) });
    }
  }
  return (
    <div className="rq-tl">
      {steps.map(function(s, i) {
        return (
          <div key={i} className={"rq-tl-step" + (s.done ? " done" : "")}>
            <span className="rq-tl-dot">{s.done ? "✓" : ""}</span>
            <span className="rq-tl-label">{s.label}</span>
            <span className="rq-tl-when">{s.done ? s.when : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── GUEST: Notification Center (bell) ─────────────────────── */
/* Derives a chronological event feed from the request documents —
   no extra collection, no extra listeners, no duplicate reads. */
function requestEvents(requests) {
  var evs = [];
  (requests || []).forEach(function(r) {
    function push(ts, icon, title) {
      if (ts) evs.push({ key: r.id + "_" + title, ts: (ts.seconds || 0), icon: icon, title: title, r: r });
    }
    push(r.createdAt, <IconSend/>, reqTypeLabel(r.type) + " request sent");
    push(r.viewingProposedAt, <IconCalendarLine/>, "Host proposed a viewing"
      + (r.proposedDate ? " · " + r.proposedDate + (r.proposedTime ? " at " + r.proposedTime : "") : ""));
    push(r.acceptedAt, <IconCheck/>, "Host accepted your request");
    push(r.declinedAt, <IconCheck/>, "Host declined your request");
    push(r.locationSharedAt, <IconPinLine/>, "Host shared the exact location");
    push(r.contactSharedAt, <IconPhoneLine/>, "Host shared their contact");
    push(r.completedAt, <IconCheck/>, "Request completed");
  });
  evs.sort(function(a, b) { return b.ts - a.ts; });
  return evs;
}

export function NotificationsPanel(props) {
  /* props: requests, onClose, onOpenRequest(request) */
  var evs = requestEvents(props.requests);
  /* Guests reach their requests through the bell — never a bottom-nav tab. */
  var [tab, setTab] = useState("updates");
  var reqs = (props.requests || []).slice().sort(function(a, b) {
    var av = (a.updatedAt && a.updatedAt.seconds) || (a.createdAt && a.createdAt.seconds) || 0;
    var bv = (b.updatedAt && b.updatedAt.seconds) || (b.createdAt && b.createdAt.seconds) || 0;
    return bv - av;
  });

  return (
    <div className="ntf-overlay" onClick={props.onClose}>
      <div className="ntf-panel" onClick={function(e) { e.stopPropagation(); }}>
        <div className="ntf-panel-head">
          <h2 className="ntf-panel-title">{tab === "updates" ? "Notifications" : "My requests"}</h2>
          <button className="ntf-close" onClick={props.onClose} aria-label="Close">×</button>
        </div>

        <div className="ntf-seg">
          <button className={"ntf-seg-btn" + (tab === "updates" ? " on" : "")}
                  onClick={function() { setTab("updates"); }}>Updates</button>
          <button className={"ntf-seg-btn" + (tab === "requests" ? " on" : "")}
                  onClick={function() { setTab("requests"); }}>
            My requests{reqs.length ? " · " + reqs.length : ""}
          </button>
        </div>

        {tab === "updates" ? (
          evs.length === 0 ? (
            <div className="rq-empty-block">
              <div className="rq-empty-ic">🔔</div>
              <div className="rq-empty-t">No notifications yet</div>
              <div className="rq-empty-s">When you send a request — or a host responds and shares their contact — it all appears here.</div>
            </div>
          ) : (
            evs.map(function(ev) {
              return (
                <button key={ev.key} className="ntf-row" onClick={function() { props.onOpenRequest(ev.r); }}>
                  <span className="ntf-ic">{ev.icon}</span>
                  <span className="ntf-text">
                    <span className="ntf-title">{ev.title}</span>
                    <span className="ntf-sub">{ev.r.listingTitle} · {ev.r.listingArea}</span>
                  </span>
                  {ev.r.unseenGuest ? <span className="ntf-dot"/> : <span className="ntf-when">{fmtWhen({ seconds: ev.ts })}</span>}
                </button>
              );
            })
          )
        ) : (
          reqs.length === 0 ? (
            <div className="rq-empty-block">
              <div className="rq-empty-ic">📨</div>
              <div className="rq-empty-t">No requests yet</div>
              <div className="rq-empty-s">When you request contact for a long-term home, you can track it here.</div>
            </div>
          ) : (
            reqs.map(function(r) {
              var sm = statusMeta(r.status);
              return (
                <button key={r.id} className="ntf-row" onClick={function() { props.onOpenRequest(r); }}>
                  {r.listingImage
                    ? <img className="ntf-thumb" src={r.listingImage} alt=""/>
                    : <span className="ntf-ic"><IconHomeLine/></span>}
                  <span className="ntf-text">
                    <span className="ntf-title">{r.listingTitle}</span>
                    <span className="ntf-sub">{r.listingArea}</span>
                    <span className={"rq-status " + sm.cls}>{sm.label}</span>
                  </span>
                  {r.unseenGuest ? <span className="ntf-dot"/> : null}
                </button>
              );
            })
          )
        )}
      </div>
    </div>
  );
}

/* ── GUEST: Request Detail — a dedicated full page ─────────── */
export function RequestDetail(props) {
  /* props: request, onBack */
  var r = props.request;
  var sm = statusMeta(r.status);
  return (
    <div className="rqd">
      <SubpageHeader title="Your request" onBack={props.onBack}/>
      <div className="rqd-body">
        <div className="rqd-prop">
          {r.listingImage ? <img src={r.listingImage} alt=""/> : <div className="rq-card-imgph"/>}
          <div className="rqd-proptext">
            <div className="rqd-title">{r.listingTitle}</div>
            <div className="rqd-sub">{r.listingArea} · GMD {(r.monthlyRent || 0).toLocaleString()} / month</div>
            <span className={"rq-status " + sm.cls}>{sm.label}</span>
          </div>
        </div>

        {r.status === "viewing_proposed" && r.proposedDate ? (
          <div className="rqd-card rqd-propose">
            <div className="rqd-h">📅 The host proposed a viewing</div>
            <div className="rqd-propose-when">{r.proposedDate}{r.proposedTime ? " at " + r.proposedTime : ""}</div>
            <div className="rq-share-v">They'll confirm your request after the viewing. Contact details are shared once they're ready to proceed.</div>
          </div>
        ) : null}

        {/* What the guest actually asked for — never confused with createdAt */}
        {r.type === "contact" && r.moveInDate ? (
          <div className="rqd-card">
            <div className="rqd-h">Your request</div>
            <div className="rqd-kv"><span>Preferred move-in</span><strong>{fmtViewingDate(r.moveInDate)}</strong></div>
          </div>
        ) : null}

        {r.type === "viewing" && (r.viewingDate || r.viewingTime) ? (
          <div className="rqd-card">
            <div className="rqd-h">Your requested viewing</div>
            {r.viewingDate ? (
              <div className="rqd-kv"><span>Viewing date</span><strong>{fmtViewingDate(r.viewingDate)}</strong></div>
            ) : null}
            {r.viewingTime ? (
              <div className="rqd-kv"><span>Requested time</span><strong>{fmtViewingTime(r.viewingTime)}</strong></div>
            ) : null}
          </div>
        ) : null}

        {r.type === "rent" && (r.moveInDate || r.durationMonths || r.occupants) ? (
          <div className="rqd-card">
            <div className="rqd-h">Your rental request</div>
            {r.moveInDate ? (
              <div className="rqd-kv"><span>Move-in date</span><strong>{fmtViewingDate(r.moveInDate)}</strong></div>
            ) : null}
            {r.durationMonths ? (
              <div className="rqd-kv"><span>Duration</span><strong>{r.durationMonths} months</strong></div>
            ) : null}
            {r.occupants ? (
              <div className="rqd-kv"><span>Occupants</span><strong>{r.occupants}</strong></div>
            ) : null}
          </div>
        ) : null}

        <div className="rqd-card">
          <div className="rqd-h">Progress</div>
          <Timeline request={r}/>
        </div>

        {r.note ? (
          <div className="rqd-card">
            <div className="rqd-h">Your message</div>
            <div className="rq-note-show">"{r.note}"</div>
          </div>
        ) : null}

        {/* ── CONTACT SHARED (step 5) ──
            Only ever rendered once the host has actually shared. */}
        {r.contactShared && r.sharedContact && r.sharedContact.phone ? (
          <div className="rqd-card rqd-contact">
            <div className="rqd-contact-top">
              <div className="rqd-contact-ic">📞</div>
              <div>
                <div className="rqd-contact-t">The host shared their contact</div>
                <div className="rqd-contact-s">You can reach them directly now.</div>
              </div>
            </div>
            <div className="rqd-contact-num">{r.sharedContact.phone}</div>
            {r.sharedContact.note ? (
              <div className="rqd-contact-note">&ldquo;{r.sharedContact.note}&rdquo;</div>
            ) : null}
            <div className="rqd-contact-btns">
              <a className="rqd-cbtn rqd-cbtn-call" href={"tel:" + r.sharedContact.phone}>Call host</a>
              {waNumber(r.sharedContact.phone) ? (
                <a className="rqd-cbtn rqd-cbtn-wa"
                   href={"https://wa.me/" + waNumber(r.sharedContact.phone)}
                   target="_blank" rel="noreferrer">Chat on WhatsApp</a>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Map section appears ONLY if the host chose to share it —
            no placeholder, no empty box when they did not. */}
        {r.locationShared && r.sharedLocation ? (
          <div className="rqd-card">
            <div className="rqd-h">📍 Exact location</div>
            <div className="rq-share-v">{r.sharedLocation.address}</div>
            {r.sharedLocation.mapsLink ? (
              <a className="rq-btn rq-btn-ok rqd-maps" href={r.sharedLocation.mapsLink} target="_blank" rel="noreferrer">Open in Google Maps</a>
            ) : null}
          </div>
        ) : null}

        {/* ── GOOD TO KNOW (step 6) — safety card, shown once contact is live ── */}
        {r.contactShared ? (
          <div className="rqd-card rqd-safety">
            <div className="rqd-h">Good to know</div>
            <ul className="rqd-safety-list">
              <li>No payment is required to send a contact request.</li>
              <li>The price shown on KUNDA is the price provided for this property.</li>
              <li>Confirm the rent, deposit and any agent fee directly with the host before making any payment.</li>
            </ul>
            <div className="rqd-safety-imp">
              <span className="rqd-safety-imp-t">Important</span>
              If anyone asks you to pay more than the amount shown on KUNDA without a clear explanation, contact KUNDA before paying.
            </div>
          </div>
        ) : null}

        <div className="ltd-trust">Everything the host shares stays inside this request — that's how KUNDA keeps both sides safe.</div>
      </div>
    </div>
  );
}

/* ── HOST: inbox with the state-machine action buttons ─────── */
export function HostRequestsPanel(props) {
  /* props: requests, onToast */
  var [busyId, setBusyId] = useState("");
  var [shareFor, setShareFor]     = useState(null);
  var [sharePhone, setSharePhone] = useState("");
  var [shareNote, setShareNote]   = useState("");
  var [shareLoc, setShareLoc]     = useState(false);
  var [shareBusy, setShareBusy]   = useState(false);
  var [shareErr, setShareErr]     = useState("");
  var list = (props.requests || []).slice().sort(function(a, b) {
    var ta = (a.updatedAt && a.updatedAt.seconds) || 0;
    var tb = (b.updatedAt && b.updatedAt.seconds) || 0;
    return tb - ta;
  });
  /* Collapsible status sections — newest activity first inside each */
  var [openSecs, setOpenSecs] = useState({ pending: true, viewing_proposed: true, contact_shared: true, accepted: true });
  function toggleSec(k) {
    setOpenSecs(function(s) {
      var n = Object.assign({}, s); n[k] = !n[k]; return n;
    });
  }
  var SECTIONS = [
    { key: "pending",           label: "Pending" },
    { key: "viewing_proposed",  label: "Viewing proposed" },
    { key: "contact_shared",    label: "Contact shared" },
    { key: "accepted",          label: "Accepted" },
    { key: "completed", label: "Completed" },
    { key: "declined",  label: "Declined" },
  ];

  async function act(r, fields, toast) {
    if (busyId) return;
    setBusyId(r.id);
    try {
      await updateDoc(doc(db, "requests", r.id),
        Object.assign({ unseenGuest: true, updatedAt: serverTimestamp() }, fields));
      props.onToast(toast);
    } catch (e) {
      props.onToast("Action failed. Check your connection and try again.");
    }
    setBusyId("");
  }

  async function shareLocation(r) {
    if (busyId) return;
    setBusyId(r.id);
    try {
      /* Only the listing's owner can read this document (security rules).
         The host tapping this button IS the authorisation to share. */
      var snap = await getDoc(doc(db, "listings", r.listingId, "private", "location"));
      var priv = snap.exists() ? snap.data() : null;
      if (!priv || !priv.address) {
        props.onToast("No private location saved for this listing.");
        setBusyId("");
        return;
      }
      await updateDoc(doc(db, "requests", r.id), {
        locationShared:   true,
        sharedLocation:   { address: priv.address, mapsLink: priv.mapsLink || "" },
        locationSharedAt: serverTimestamp(),
        unseenGuest:      true,
        updatedAt:        serverTimestamp(),
      });
      props.onToast("Exact location shared with the guest 📍");
    } catch (e) {
      props.onToast("Couldn't share the location. Try again.");
    }
    setBusyId("");
  }

  /* "Arrange a viewing first" — the request stays OPEN. The host proposes a
     date/time; they still decide accept/decline afterwards. */
  function proposeViewing(r) {
    var d = window.prompt("Propose a viewing date (YYYY-MM-DD):", r.viewingDate || "");
    if (!d || !d.trim()) return;
    var t = window.prompt("Propose a time (e.g. 15:00):", r.viewingTime || "");
    if (!t || !t.trim()) return;
    act(r, {
      status:            "viewing_proposed",
      proposedDate:      d.trim(),
      proposedTime:      t.trim(),
      viewingProposedAt: serverTimestamp(),
    }, "Viewing proposed — the guest has been notified 📅");
  }

  /* ── HOST SHARE SCREEN (step 4) ──
     One deliberate action: the host types the number, optionally attaches
     the exact location, optionally adds a note, and shares. Everything
     lands in one write so the guest never sees a half-shared state. */
  function openShare(r) {
    setShareFor(r);
    setSharePhone("");
    setShareNote("");
    setShareLoc(false);
    setShareErr("");
  }

  async function submitShare() {
    if (!shareFor || shareBusy) return;
    if (sharePhone.trim().length < 6) {
      setShareErr("Please enter a valid phone number.");
      return;
    }
    setShareErr("");
    setShareBusy(true);
    try {
      var patch = {
        status:          "contact_shared",
        contactShared:   true,
        sharedContact:   { phone: sharePhone.trim(), note: shareNote.trim() },
        contactSharedAt: serverTimestamp(),
        unseenGuest:     true,
        updatedAt:       serverTimestamp(),
      };
      /* Optional: attach the exact location in the same write */
      if (shareLoc) {
        var snap = await getDoc(doc(db, "listings", shareFor.listingId, "private", "location"));
        var priv = snap.exists() ? snap.data() : null;
        if (priv && priv.address) {
          patch.locationShared   = true;
          patch.sharedLocation   = { address: priv.address, mapsLink: priv.mapsLink || "" };
          patch.locationSharedAt = serverTimestamp();
        } else {
          setShareErr("No private location is saved for this listing, so it can't be shared.");
          setShareBusy(false);
          return;
        }
      }
      await updateDoc(doc(db, "requests", shareFor.id), patch);
      setShareFor(null);
      props.onToast("Contact shared with the guest 📞");
    } catch (e) {
      console.error("Share contact failed:", e && e.code);
      setShareErr("Couldn't share right now. Please check your connection and try again.");
    }
    setShareBusy(false);
  }

  function shareContact(r) {
    var phone = window.prompt("Phone number to share with this guest (WhatsApp works best):", "");
    if (!phone || !phone.trim()) return;
    act(r, {
      contactShared:   true,
      sharedContact:   { phone: phone.trim() },
      contactSharedAt: serverTimestamp(),
    }, "Your contact was shared with the guest 📞");
  }

  return (
    <div className="rq-host">
      <h2 className="rq-host-h">Requests</h2>
      {list.length === 0 ? (
        <div className="rq-empty-block">
          <div className="rq-empty-ic">📥</div>
          <div className="rq-empty-t">No requests yet</div>
          <div className="rq-empty-s">Contact requests for your long-term listings will appear here the moment a guest reaches out.</div>
        </div>
      ) : (
        SECTIONS.map(function(sec) {
          var items = list.filter(function(r) { return r.status === sec.key; });
          if (items.length === 0) return null;
          var open = !!openSecs[sec.key];
          return (
            <div key={sec.key} className="rqh-sec">
              <button className="rqh-sec-head" onClick={function() { toggleSec(sec.key); }}>
                <span className="rqh-sec-label">{sec.label}</span>
                <span className="rqh-sec-count">{items.length}</span>
                <span className={"rqh-sec-chev" + (open ? " open" : "")}>▾</span>
              </button>
              {open && items.map(function(r) {
          var sm = statusMeta(r.status);
          var b = busyId === r.id;
          return (
            <div key={r.id} className={"rq-card" + (r.unseenHost ? " rq-new" : "")}>
              <div className="rq-card-head">
                {r.listingImage ? <img src={r.listingImage} alt="" loading="lazy"/> : <div className="rq-card-imgph"/>}
                <div className="rq-card-headtext">
                  <div className="rq-card-title">{r.guestName} · {reqIntent(r.type)}</div>
                  <div className="rq-card-sub">{r.listingTitle} · {r.listingArea} · {fmtWhen(r.createdAt)}</div>
                </div>
                <span className={"rq-status " + sm.cls}>{sm.label}</span>
              </div>
              {r.type === "contact" && r.moveInDate ? (
                <div className="rq-rent-meta">
                  <span>📅 Wants to move in {fmtViewingDate(r.moveInDate)}</span>
                </div>
              ) : null}
              {r.type === "rent" && (r.moveInDate || r.durationMonths || r.occupants) ? (
                <div className="rq-rent-meta">
                  {r.moveInDate ? <span>📅 Move in {fmtViewingDate(r.moveInDate)}</span> : null}
                  {r.durationMonths ? <span>⏱ {r.durationMonths} months</span> : null}
                  {r.occupants ? <span>👥 {r.occupants} {r.occupants === 1 ? "occupant" : "occupants"}</span> : null}
                </div>
              ) : null}
              {r.type === "viewing" && (r.viewingDate || r.viewingTime) ? (
                <div className="rq-rent-meta">
                  {r.viewingDate ? <span>📅 Viewing date: {fmtViewingDate(r.viewingDate)}</span> : null}
                  {r.viewingTime ? <span>🕐 Requested time: {fmtViewingTime(r.viewingTime)}</span> : null}
                </div>
              ) : null}
              {r.note ? <div className="rq-note-show">"{r.note}"</div> : null}
              <Timeline request={r} forHost/>
              <div className="rq-actions">
                {r.status === "pending" && r.type === "contact" && (
                  <>
                    <button className="rq-btn rq-btn-ok" disabled={b}
                            onClick={function() { openShare(r); }}>
                      Share contact with guest
                    </button>
                    <button className="rq-btn rq-btn-no" disabled={b}
                            onClick={function() { act(r, { status: "declined", declinedAt: serverTimestamp() }, "Request declined"); }}>
                      Decline
                    </button>
                  </>
                )}
                {r.status === "pending" && r.type !== "contact" && (
                  <>
                    <button className="rq-btn rq-btn-ok" disabled={b}
                            onClick={function() { act(r, { status: "accepted", acceptedAt: serverTimestamp() }, "You're interested — the guest has been notified ✓"); }}>
                      I'm interested
                    </button>
                    <button className="rq-btn rq-btn-alt" disabled={b}
                            onClick={function() { proposeViewing(r); }}>
                      Arrange a viewing first
                    </button>
                    <button className="rq-btn rq-btn-no" disabled={b}
                            onClick={function() { act(r, { status: "declined", declinedAt: serverTimestamp() }, "Request declined"); }}>
                      Decline request
                    </button>
                  </>
                )}
                {r.status === "viewing_proposed" && (
                  <>
                    <button className="rq-btn rq-btn-ok" disabled={b}
                            onClick={function() { act(r, { status: "accepted", acceptedAt: serverTimestamp() }, "Accepted — you can now share your location ✓"); }}>
                      I'm interested
                    </button>
                    <button className="rq-btn rq-btn-alt" disabled={b}
                            onClick={function() { proposeViewing(r); }}>
                      Change viewing time
                    </button>
                    <button className="rq-btn rq-btn-no" disabled={b}
                            onClick={function() { act(r, { status: "declined", declinedAt: serverTimestamp() }, "Request declined"); }}>
                      Decline request
                    </button>
                  </>
                )}
                {r.status === "accepted" && !r.locationShared && (
                  <button className="rq-btn rq-btn-ok" disabled={b} onClick={function() { shareLocation(r); }}>
                    Share exact location
                  </button>
                )}
                {r.status === "accepted" && r.locationShared && !r.contactShared && (
                  <button className="rq-btn rq-btn-ok" disabled={b} onClick={function() { shareContact(r); }}>
                    Share my contact
                  </button>
                )}
                {r.status === "accepted" && r.locationShared && r.contactShared && (
                  <button className="rq-btn rq-btn-done" disabled={b}
                          onClick={function() { act(r, { status: "completed", completedAt: serverTimestamp() }, "Marked as completed ✓"); }}>
                    Mark completed
                  </button>
                )}
              </div>
            </div>
              );
              })}
            </div>
          );
        })
      )}

      {/* ── SHARE CONTACT SHEET (step 4) ── */}
      {shareFor && (
        <div className="bsheet-overlay" onClick={function() { if (!shareBusy) setShareFor(null); }}>
          <div className="bsheet" onClick={function(e) { e.stopPropagation(); }}>
            <div className="bsheet-handle"/>
            <h2 className="bsheet-title">Share your contact</h2>
            <div className="bsheet-prop">{shareFor.fullName || shareFor.guestName} · {shareFor.listingTitle}</div>

            <label className="bsheet-field">
              <span className="bsheet-field-label">Phone number</span>
              <input className="wiz-input" type="tel" inputMode="tel" value={sharePhone} maxLength={20}
                     placeholder="e.g. 220 xxx xxxx"
                     onChange={function(e) { setSharePhone(e.target.value); }}/>
            </label>
            <div className="rq-privacy-note">This is shared only with this guest, only when you tap share.</div>

            <button type="button" className={"rq-toggle-row" + (shareLoc ? " on" : "")}
                    onClick={function() { setShareLoc(!shareLoc); }}>
              <span className="rq-toggle-text">
                <span className="rq-toggle-t">Share exact location</span>
                <span className="rq-toggle-s">Optional — sends the saved address and map link</span>
              </span>
              <span className="rq-toggle-sw"><span className="rq-toggle-knob"/></span>
            </button>

            <label className="bsheet-field">
              <span className="bsheet-field-label">Add a note (optional)</span>
              <textarea className="wiz-textarea" maxLength={300} value={shareNote}
                        placeholder="e.g. Best to call after 5pm."
                        onChange={function(e) { setShareNote(e.target.value); }}/>
            </label>

            {shareErr ? <div className="ltw-error">{shareErr}</div> : null}

            <button className="bsheet-continue" onClick={submitShare} disabled={shareBusy}>
              {shareBusy ? "Sharing…" : "Share with guest"}
            </button>
            <button className="rq-btn rq-btn-alt" disabled={shareBusy}
                    onClick={function() { setShareFor(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── The bell (navbar) ─────────────────────────────────────── */
function BellButtonBase(props) {
  /* props: count, onClick */
  return (
    <button className="bell-btn" onClick={props.onClick} aria-label="Your requests">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.7 21a2 2 0 0 1-3.4 0"/>
      </svg>
      {props.count > 0 && <span className="bell-badge">{props.count > 9 ? "9+" : props.count}</span>}
    </button>
  );
}
/* Memoized — the bell sits in the navbar and would otherwise re-render on
   every App state change; it only needs to react to the unread count. */
export var BellButton = React.memo(BellButtonBase, function(a, b) {
  return a.count === b.count;
});
